import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { TranslationModel } from '@models/translation';
import { TranslationPlatform } from '@models/system';
import { HubHandlerSourceAngular } from '@models/system/signalR';
import { HttpService } from '@shared/services';
import { TranslateService } from '@ngx-translate/core';
import { SignalRService } from './signalR.service';
import { TranslationBundles, TranslationBundlesService } from './translation-bundles.service';
import { environment } from 'src/environments/environment';

export type TranslationExportFormat = 'json' | 'csv' | 'zip';

export type { TranslationBundles };

@Injectable({ providedIn: 'root' })
export class TranslationService {
  private readonly httpClient = inject(HttpClient);
  private readonly http = inject(HttpService);
  private readonly translateService = inject(TranslateService);
  private readonly signalRService = inject(SignalRService);
  private readonly translationBundlesService = inject(TranslationBundlesService);

  /** Registers the SignalR handler for live translation updates. Call once on app init. */
  registerSignalRHandler(): void {
    this.signalRService.registerHandler(
      HubHandlerSourceAngular,
      'TranslationsUpdated',
      (platform: number, languageCode: string) => {
        if (platform === TranslationPlatform.Angular) {
          this.translateService.reloadLang(languageCode).subscribe();
        }
      },
    );
  }

  /**
   * Returns translation bundles for the given language and platform(s), grouped by platform name.
   * This is the single entry point for fetching platform-specific translation bundles (e.g. Angular
   * UI labels, error codes, or the Nutrient PDF viewer's locale strings) and should be used everywhere
   * a set of translations needs to be loaded from the backend — e.g. the ngx-translate loader
   * (`TranslationApiLoader`) and `NutrientService`.
   */
  getBundles(languageCode: string, platforms: TranslationPlatform[]): Observable<TranslationBundles> {
    return this.translationBundlesService.getBundles(languageCode, platforms);
  }

  /** Returns translation entries for the given language for the sys-admin editor. */
  getTranslations(languageCode: string) {
    return this.http.get<TranslationModel[]>(`_api/translations/all?languageCode=${encodeURIComponent(languageCode)}`, {
      useLoader: false,
    });
  }

  /** Returns all language entries for a given platform + key, used to prefill the edit dialog. */
  getByKey(platform: number, key: string) {
    return this.http.get<TranslationModel[]>(
      `_api/translations/bykey?platform=${platform}&key=${encodeURIComponent(key)}`,
      { useLoader: false },
    );
  }

  /** Creates or updates all language values for a key in one call. */
  upsertBulk(models: TranslationModel[]) {
    return this.http.put<void>('_api/translations', models, { useLoader: false });
  }

  /** Deletes all language entries for a key. */
  deleteByKey(platform: number, key: string) {
    return this.http.delete(`_api/translations/bykey?platform=${platform}&key=${encodeURIComponent(key)}`, {
      useLoader: false,
    });
  }

  /** Triggers a file download of all translations in the requested format. */
  export(format: TranslationExportFormat, zipFormat: 'json' | 'csv' = 'json'): void {
    const baseUrl = environment.baseApiUrl.replace(/\/$/, '');
    const url = `${baseUrl}/_api/translations/export?format=${format}&zipFormat=${zipFormat}`;

    this.httpClient.get(url, { responseType: 'blob' }).subscribe(blob => {
      const downloadLink = document.createElement('a');
      downloadLink.href = URL.createObjectURL(blob);
      downloadLink.download = `translations.${format}`;
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
      URL.revokeObjectURL(downloadLink.href);
    });
  }

  /** Uploads a translation file for import. Returns the number of rows upserted. */
  import(file: File): Observable<number> {
    const ext = file.name.split('.').pop()?.toLowerCase() ?? 'json';
    const format = ext === 'zip' ? 'zip' : ext === 'csv' ? 'csv' : 'json';
    const formData = new FormData();
    formData.append('file', file);
    return this.http.post<number>(`_api/translations/import?format=${format}`, formData, { useLoader: false });
  }
}
